import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put, select } from 'redux-saga/effects';

// Fetch the inbox conversation list using the current filters in the store
function* getInboxConversations({ payload }) {
  const append = payload?.['append'] || false;
  if (!append) yield put({ type: 'SET_INBOX_LOADING', payload: true });

  const filters = yield select(state => state['Inbox']['filters']);
  const params = new URLSearchParams();
  params.append('channel', filters['channel'] || 'all');
  if (filters['account']) params.append('account', filters['account']);
  params.append('status', filters['status'] || 'all');
  if (filters['search']) params.append('search', filters['search']);
  params.append('limit', filters['limit'] || 30);
  params.append('skip', filters['skip'] || 0);

  const { error, response } = yield call(getCall, `/activities/inbox/conversations?${params.toString()}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch conversations');
    yield put({ type: 'SET_INBOX_LOADING', payload: false });
    return;
  }
  const body = response['data']['body'] || {};
  yield put({ type: 'SET_INBOX_CONVERSATIONS', payload: { conversations: body['conversations'] || [], total: body['total'] || 0, connectedChannels: body['connectedChannels'] || {}, append } });
}

// Fetch the global unread total for the sidebar badge
function* getInboxUnreadCount() {
  const { error, response } = yield call(getCall, '/activities/inbox/unread-count');
  if (error) return;
  yield put({ type: 'SET_INBOX_UNREAD_TOTAL', payload: response['data']['body']?.['total'] || 0 });
}

// Mark a contact's inbound messages as read (optimistic row clear, then reconcile the total)
function* markInboxRead({ payload: contactId }) {
  yield put({ type: 'MARK_INBOX_ROW_READ', payload: contactId });
  const { error, response } = yield call(postCall, { path: `/activities/inbox/read/${contactId}`, payload: {} });
  if (error) return;
  yield put({ type: 'SET_INBOX_UNREAD_TOTAL', payload: response['data']['body']?.['total'] || 0 });
}

// Mark a contact's conversation unread (optimistic row bump to 1, then reconcile the total)
function* markInboxUnread({ payload: contactId }) {
  yield put({ type: 'MARK_INBOX_ROW_UNREAD', payload: contactId });
  const { error, response } = yield call(postCall, { path: `/activities/inbox/unread/${contactId}`, payload: {} });
  if (error) return;
  yield put({ type: 'SET_INBOX_UNREAD_TOTAL', payload: response['data']['body']?.['total'] || 0 });
}

// Mark a single message unread from the thread (optimistic row bump to 1, then reconcile the total)
function* markInboxMessageUnread({ payload: { activityId, contactId } }) {
  if (contactId) yield put({ type: 'MARK_INBOX_ROW_UNREAD', payload: contactId });
  const { error, response } = yield call(postCall, { path: `/activities/inbox/unread-message/${activityId}`, payload: {} });
  if (error) return;
  yield put({ type: 'SET_INBOX_UNREAD_TOTAL', payload: response['data']['body']?.['total'] || 0 });
}

// Load the contact for the active conversation into the dedicated Inbox slot (not SinglePage)
function* getInboxContact({ payload: contactId }) {
  yield put({ type: 'SET_INBOX_CONTACT_LOADING', payload: true });
  const { error, response } = yield call(getCall, `/objects/contacts/${contactId}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to load contact');
    yield put({ type: 'SET_INBOX_CONTACT_LOADING', payload: false });
    return;
  }
  yield put({ type: 'SET_INBOX_ACTIVE_CONTACT', payload: response['data']['body'] });
}

function* actionWatcher() {
  yield takeEvery('GET_INBOX_CONVERSATIONS', getInboxConversations);
  yield takeEvery('GET_INBOX_UNREAD_COUNT', getInboxUnreadCount);
  yield takeEvery('MARK_INBOX_READ', markInboxRead);
  yield takeEvery('MARK_INBOX_UNREAD', markInboxUnread);
  yield takeEvery('MARK_INBOX_MESSAGE_UNREAD', markInboxMessageUnread);
  yield takeEvery('GET_INBOX_CONTACT', getInboxContact);
}

export default function* rootSaga() { yield all([actionWatcher()]) }

function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

function postCall({ path, payload }) {
  return axios
    .post(path, payload, { headers: { 'Content-Type': 'application/json' } })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}
